const slideBase = {
  flow: 'y',
  align: 'center center',
  justifyContent: 'center',
  padding: 'D',
  gap: 'B',
  textAlign: 'center',
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: '0',
  left: '0',
  transition: 'opacity 0.5s ease, transform 0.5s ease',
  '@mobileL': { padding: 'C A' }
}

const cardBase = {
  flow: 'y',
  align: 'center center',
  padding: 'B',
  background: 'surfaceCard',
  border: '1px solid surfaceBorderStrong',
  borderRadius: 'Z',
  gap: 'Z',
  flex: '1',
  minWidth: '200px'
}

const painBase = {
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'A B',
  background: 'redBg',
  border: '1px solid redBorder',
  borderRadius: 'Z',
  textAlign: 'left',
  Dot: {
    width: 'Z',
    height: 'Z',
    borderRadius: '100%',
    background: 'red',
    flexShrink: '0',
  },
  PainText: {
    color: 'text',
    fontSize: 'C',
    textAlign: 'left'
  }
}

const pillarBase = {
  flow: 'y',
  gap: 'A',
  padding: 'B',
  borderRadius: 'Z',
  flex: '1',
  minWidth: '200px',
  transition: 'border-color 200ms',
  PillarTitle: {
    tag: 'h3',
    fontSize: 'D',
    fontWeight: '600'
  },
  childExtends: {
    color: 'textMuted',
    fontSize: 'B',
    lineHeight: '1.7'
  }
}

const tierBase = {
  flow: 'y',
  align: 'center center',
  gap: 'Z',
  padding: 'B',
  background: 'surfaceCard',
  border: '1px solid surfaceBorderStrong',
  borderRadius: 'Z',
  flex: '1',
  minWidth: '180px',
  textAlign: 'center',
  TierLabel: {
    color: 'textSoft',
    fontSize: 'B',
    fontWeight: '600'
  },
  TierTarget: {
    color: 'textMuted',
    fontSize: 'A'
  },
  TierPrice: {
    tag: 'span',
    fontSize: 'F',
    fontWeight: '700',
    color: 'text'
  },
  TierPer: {
    text: '{{ pitchPerMonth | polyglot }}',
    color: 'textMuted',
    fontSize: 'A'
  }
}

const whyBase = {
  flow: 'x',
  align: 'center',
  gap: 'B',
  padding: 'B',
  background: 'surfaceCard',
  border: '1px solid surfaceBorderStrong',
  borderRadius: 'Z',
  WhyNum: {
    tag: 'span',
    fontSize: 'D',
    fontWeight: '700',
    color: 'green',
    minWidth: 'C',
    fontFamily: 'monospace'
  },
  WhyDesc: {
    color: 'text',
    fontSize: 'C',
    textAlign: 'left'
  }
}

const slideAnim = (idx) => ({
  opacity: (el, s) => s.slide === idx ? '1' : '0',
  transform: (el, s) => {
    if (s.slide === idx) return 'translateX(0) scale(1)'
    if (s.slide > idx) return 'translateX(-60px) scale(0.97)'
    return 'translateX(60px) scale(0.97)'
  },
  pointerEvents: (el, s) => s.slide === idx ? 'auto' : 'none'
})

export const pitch = {
  extends: 'Page',
  background: 'surface',
  overflow: 'hidden',
  position: 'relative',
  flow: 'y',
  height: '100vh',
  state: {
    slide: 0,
    total: 12
  },
  SlideCounter: {
    position: 'absolute',
    top: 'A',
    right: 'B',
    zIndex: '10',
    fontSize: 'Z',
    color: 'textMuted',
    fontFamily: 'monospace',
    text: (el, s) => (s.slide + 1) + ' / ' + s.total
  },
  SlidesWrap: {
    width: '100%',
    flex: '1',
    overflow: 'hidden',
    position: 'relative',

    // Slide 0 — Hero
    Slide0: {
      ...slideBase,
      ...slideAnim(0),
      gap: 'B',
      TagLine: {
        fontSize: 'A',
        color: 'green',
        fontWeight: '600',
        text: '{{ pitchTagline | polyglot }}',
        letterSpacing: '1px',
        textTransform: 'uppercase'
      },
      TitleText: {
        tag: 'h1',
        text: '{{ pitchTitle | polyglot }}',
        fontSize: 'G',
        fontWeight: '700',
        color: 'text',
        lineHeight: '1.1',
        '@mobileL': { fontSize: 'E' }
      },
      SubText: {
        text: '{{ pitchSubtitle | polyglot }}',
        fontSize: 'C',
        color: 'textMuted',
        maxWidth: 'H',
        lineHeight: '1.6'
      },
      CtaBtn: {
        extends: 'Button',
        text: '{{ pitchCta | polyglot }}',
        background: 'green',
        color: 'black',
        padding: 'A B',
        fontSize: 'B',
        fontWeight: '600',
        borderRadius: 'Y',
        border: 'none',
        cursor: 'pointer',
        marginTop: 'A',
        onClick: (e, el, s) => s.update({ slide: 1 })
      }
    },

    // Slide 1 — Market stats
    Slide1: {
      ...slideBase,
      ...slideAnim(1),
      SlideLabel: {
        text: '{{ pitchMarketLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchMarketTitle | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      StatsRow: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        '@mobileL': { flow: 'y', width: '100%' },
        StatCard1: {
          ...cardBase,
          StatVal: {
            tag: 'span',
            text: '3.8M',
            fontSize: 'F',
            fontWeight: '700',
            color: 'green'
          },
          StatLbl: { text: '{{ pitchPopulation | polyglot }}', color: 'textMuted', fontSize: 'B' }
        },
        StatCard2: {
          ...cardBase,
          StatVal: {
            tag: 'span',
            text: '{{ pitchTbilisi | polyglot }}',
            fontSize: 'F',
            fontWeight: '700',
            color: 'green'
          },
          StatLbl: {
            text: '{{ pitchConcentrated | polyglot }}',
            color: 'textMuted',
            fontSize: 'B'
          }
        },
        StatCard3: {
          ...cardBase,
          StatVal: {
            tag: 'span',
            text: '0',
            fontSize: 'F',
            fontWeight: '700',
            color: 'green'
          },
          StatLbl: {
            text: '{{ pitchZeroCompetitor | polyglot }}',
            color: 'textMuted',
            fontSize: 'B'
          }
        }
      }
    },

    // Slide 2 — Pain points
    Slide2: {
      ...slideBase,
      ...slideAnim(2),
      SlideLabel: {
        text: '{{ pitchProblemLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchProblemTitle | polyglot }}',
        fontSize: 'E',
        fontWeight: '700',
        color: 'text',
        maxWidth: 'I',
        '@mobileL': { fontSize: 'C' }
      },
      PainList: {
        flow: 'y',
        gap: 'A',
        maxWidth: 'H',
        width: '100%',
        Pain1: {
          ...painBase,
          PainText: {
            ...painBase.PainText,
            text: '{{ pitchNoBooking | polyglot }}'
          }
        },
        Pain2: {
          ...painBase,
          PainText: { ...painBase.PainText, text: '{{ pitchManualCases | polyglot }}' }
        },
        Pain3: {
          ...painBase,
          PainText: {
            ...painBase.PainText,
            text: '{{ pitchNoAiTools | polyglot }}'
          }
        }
      }
    },

    // Slide 3 — Three pillars
    Slide3: {
      ...slideBase,
      ...slideAnim(3),
      SlideLabel: {
        text: '{{ pitchCapabilitiesLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchOnePlatform | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      PillarsRow: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },
        PillarBooking: {
          ...pillarBase,
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          ':hover': { borderColor: 'green' },
          PillarNum: { text: '01', fontSize: 'Z', color: 'green', fontFamily: 'monospace' },
          PillarTitle: {
            ...pillarBase.PillarTitle,
            text: '{{ pitchBooking | polyglot }}',
            color: 'text'
          },
          Feat1: { text: '{{ pitchClientSchedule | polyglot }}' },
          Feat2: { text: '{{ pitchCalendarSync | polyglot }}' },
          Feat3: { text: '{{ pitchAutoReminder | polyglot }}' }
        },
        PillarOps: {
          ...pillarBase,
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          ':hover': { borderColor: 'green' },
          PillarNum: { text: '02', fontSize: 'Z', color: 'green', fontFamily: 'monospace' },
          PillarTitle: {
            ...pillarBase.PillarTitle,
            text: '{{ pitchOperations | polyglot }}',
            color: 'text'
          },
          Feat1: { text: '{{ pitchCaseManagement | polyglot }}' },
          Feat2: { text: '{{ pitchInvoices | polyglot }}' },
          Feat3: { text: '{{ pitchTrustedAccounting | polyglot }}' }
        },
        PillarAI: {
          ...pillarBase,
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          ':hover': { borderColor: 'green' },
          PillarNum: { text: '03', fontSize: 'Z', color: 'green', fontFamily: 'monospace' },
          PillarTitle: {
            ...pillarBase.PillarTitle,
            text: 'AI',
            color: 'text'
          },
          Feat1: { text: '{{ pitchDocDraftGeo | polyglot }}' },
          Feat2: { text: '{{ pitchContractAnalysis | polyglot }}' },
          Feat3: { text: '{{ pitchAutoNextSteps | polyglot }}' }
        }
      }
    },

    // Slide 4 — Product & Perks
    Slide4: {
      ...slideBase,
      ...slideAnim(4),
      SlideLabel: {
        text: '{{ pitchProductLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchProductTitle | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      PerksRow: {
        flow: 'x',
        gap: 'Z',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },
        childExtends: {
          flow: 'y',
          align: 'center center',
          gap: 'Y',
          padding: 'A',
          background: 'surfaceCard',
          border: '1px solid surfaceBorderStrong',
          borderRadius: 'Z',
          flex: '1',
          minWidth: '140px',
          textAlign: 'center',
          transition: 'border-color 200ms',
          ':hover': { borderColor: 'green' }
        },
        Perk1: {
          PerkIcon: { text: '📅', fontSize: 'C' },
          PerkName: { text: '{{ pitchPerkBookings | polyglot }}', fontSize: 'A', fontWeight: '600', color: 'text' }
        },
        Perk2: {
          PerkIcon: { text: '🤖', fontSize: 'C' },
          PerkName: { text: '{{ pitchPerkAiDocs | polyglot }}', fontSize: 'A', fontWeight: '600', color: 'text' }
        },
        Perk3: {
          PerkIcon: { text: '📱', fontSize: 'C' },
          PerkName: { text: '{{ pitchPerkResponsive | polyglot }}', fontSize: 'A', fontWeight: '600', color: 'text' }
        },
        Perk4: {
          PerkIcon: { text: '📁', fontSize: 'C' },
          PerkName: { text: '{{ pitchPerkFileStorage | polyglot }}', fontSize: 'A', fontWeight: '600', color: 'text' }
        },
        Perk5: {
          PerkIcon: { text: '💬', fontSize: 'C' },
          PerkName: { text: '{{ pitchPerkCommunication | polyglot }}', fontSize: 'A', fontWeight: '600', color: 'text' }
        },
        Perk6: {
          PerkIcon: { text: '👤', fontSize: 'C' },
          PerkName: { text: '{{ pitchPerkClientPortal | polyglot }}', fontSize: 'A', fontWeight: '600', color: 'text' }
        }
      }
    },

    // Slide 5 — Integrations
    Slide5: {
      ...slideBase,
      ...slideAnim(5),
      SlideLabel: {
        text: '{{ pitchIntegrationsLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchIntegrationsTitle | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      SlideSub: {
        text: '{{ pitchIntegrationsSub | polyglot }}',
        fontSize: 'B',
        color: 'textMuted',
        maxWidth: 'H',
        lineHeight: '1.6'
      },
      IntegrationsGrid: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },

        IntGov1: {
          ...cardBase,
          IntIcon: { text: '⚖️', fontSize: 'C' },
          IntTitle: { tag: 'h3', text: 'ecourt.ge', fontSize: 'A', fontWeight: '700', color: 'text' },
          IntDesc: { text: '{{ pitchEcourtDesc | polyglot }}', color: 'textMuted', fontSize: 'Z', lineHeight: '1.6' }
        },
        IntGov2: {
          ...cardBase,
          IntIcon: { text: '🏛️', fontSize: 'C' },
          IntTitle: { tag: 'h3', text: '{{ pitchPublicRegistry | polyglot }}', fontSize: 'A', fontWeight: '700', color: 'text' },
          IntDesc: { text: '{{ pitchPublicRegistryDesc | polyglot }}', color: 'textMuted', fontSize: 'Z', lineHeight: '1.6' }
        },
        IntGov3: {
          ...cardBase,
          IntIcon: { text: '📜', fontSize: 'C' },
          IntTitle: { tag: 'h3', text: 'matsne.gov.ge', fontSize: 'A', fontWeight: '700', color: 'text' },
          IntDesc: { text: '{{ pitchMatsneDesc | polyglot }}', color: 'textMuted', fontSize: 'Z', lineHeight: '1.6' }
        },
      },
      IntegrationsRow2: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },

        IntGov4: {
          ...cardBase,
          IntIcon: { text: '💰', fontSize: 'C' },
          IntTitle: { tag: 'h3', text: 'rs.ge', fontSize: 'A', fontWeight: '700', color: 'text' },
          IntDesc: { text: '{{ pitchRsGeDesc | polyglot }}', color: 'textMuted', fontSize: 'Z', lineHeight: '1.6' }
        },
        IntCal: {
          ...cardBase,
          background: 'greenBg',
          border: '2px solid green',
          IntIcon: { text: '📅', fontSize: 'C' },
          IntTitle: { tag: 'h3', text: '{{ pitchCalendarSyncTitle | polyglot }}', fontSize: 'A', fontWeight: '700', color: 'text' },
          IntDesc: { text: '{{ pitchCalendarSyncDesc | polyglot }}', color: 'textSoft', fontSize: 'Z', lineHeight: '1.6' }
        },
        IntOcr: {
          ...cardBase,
          IntIcon: { text: '🔍', fontSize: 'C' },
          IntTitle: { tag: 'h3', text: '{{ pitchOcrTitle | polyglot }}', fontSize: 'A', fontWeight: '700', color: 'text' },
          IntDesc: { text: '{{ pitchOcrDesc | polyglot }}', color: 'textMuted', fontSize: 'Z', lineHeight: '1.6' }
        },
      },
    },

    // Slide 6 — Pricing
    Slide6: {
      ...slideBase,
      ...slideAnim(6),
      SlideLabel: {
        text: '{{ pitchPlansLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchPlansTitle | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      RateNote: {
        text: '{{ pitchRateNote | polyglot }}',
        color: 'textMuted',
        fontSize: 'B'
      },
      TiersRow: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },
        TierStarter: {
          ...tierBase,
          TierLabel: { ...tierBase.TierLabel, text: '{{ pitchTierStarter | polyglot }}' },
          TierTarget: { ...tierBase.TierTarget, text: '{{ pitchTierStarter12 | polyglot }}' },
          TierPrice: { ...tierBase.TierPrice, text: '₾15' }
        },
        TierPro: {
          ...tierBase,
          background: 'greenBg',
          border: '2px solid green',
          TierBadge: {
            flow: 'x',
            gap: 'Y',
            align: 'center',
            BadgeDot: { width: 'Z', height: 'Z', background: 'green' },
            BadgeText: { text: '{{ pitchMostPopular | polyglot }}', color: 'green', fontSize: 'Y', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }
          },
          TierLabel: { ...tierBase.TierLabel, text: '{{ pitchTierPro | polyglot }}', color: 'text' },
          TierTarget: { ...tierBase.TierTarget, text: '{{ pitchTierPro310 | polyglot }}' },
          TierPrice: { ...tierBase.TierPrice, text: '₾75', color: 'green' }
        },
        TierBusiness: {
          ...tierBase,
          TierLabel: { ...tierBase.TierLabel, text: '{{ pitchTierBusiness | polyglot }}' },
          TierTarget: { ...tierBase.TierTarget, text: '{{ pitchTierBusiness10 | polyglot }}' },
          TierPrice: { ...tierBase.TierPrice, text: '₾299' }
        },
        TierEnterprise: {
          ...tierBase,
          TierLabel: { ...tierBase.TierLabel, text: '{{ pitchTierEnterprise | polyglot }}' },
          TierTarget: { ...tierBase.TierTarget, text: '{{ pitchTierEnterpriseCustom | polyglot }}' },
          TierPrice: { ...tierBase.TierPrice, text: '{{ pitchTierEnterprisePrice | polyglot }}', fontSize: 'D' }
        }
      }
    },

    // Slide 7 — Revenue potential
    Slide7: {
      ...slideBase,
      ...slideAnim(7),
      SlideLabel: {
        text: '{{ pitchRevenueLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchRevenueTitle | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      CalcNote: {
        text: '{{ pitchCalcNote | polyglot }}',
        color: 'textMuted',
        fontSize: 'A',
        fontStyle: 'italic'
      },
      ScenarioRow: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        '@mobileL': { flow: 'y', width: '100%' },
        Scenario1: {
          ...cardBase,
          ScenPct: { tag: 'span', text: '5%', fontSize: 'F', fontWeight: '700', color: 'textMuted' },
          ScenCapture: { text: '{{ pitchScen250 | polyglot }}', color: 'textMuted', fontSize: 'A' },
          ScenArr: { tag: 'span', text: '₾105K ARR', fontSize: 'C', fontWeight: '700', color: 'green' }
        },
        Scenario2: {
          ...cardBase,
          background: 'greenBg',
          border: '2px solid green',
          ScenPct: { tag: 'span', text: '10%', fontSize: 'F', fontWeight: '700', color: 'green' },
          ScenCapture: { text: '{{ pitchScen500 | polyglot }}', color: 'textSoft', fontSize: 'A' },
          ScenArr: { tag: 'span', text: '₾210K ARR', fontSize: 'C', fontWeight: '700', color: 'green' }
        },
        Scenario3: {
          ...cardBase,
          ScenPct: { tag: 'span', text: '20%', fontSize: 'F', fontWeight: '700', color: 'textMuted' },
          ScenCapture: { text: '{{ pitchScen1000 | polyglot }}', color: 'textMuted', fontSize: 'A' },
          ScenArr: { tag: 'span', text: '₾420K ARR', fontSize: 'C', fontWeight: '700', color: 'green' }
        }
      }
    },

    // Slide 8 — Unit Economics
    Slide8: {
      ...slideBase,
      ...slideAnim(8),
      SlideLabel: {
        text: '{{ pitchFinanceLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchFinanceTitle | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      MetricsRow: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },
        Metric1: {
          ...cardBase,
          MetricVal: { tag: 'span', text: '₾35', fontSize: 'F', fontWeight: '700', color: 'green' },
          MetricLbl: { text: '{{ pitchArpuMonth | polyglot }}', color: 'textMuted', fontSize: 'A' }
        },
        Metric2: {
          ...cardBase,
          MetricVal: { tag: 'span', text: '₾420', fontSize: 'F', fontWeight: '700', color: 'green' },
          MetricLbl: { text: '{{ pitchArpuYear | polyglot }}', color: 'textMuted', fontSize: 'A' }
        },
        Metric3: {
          ...cardBase,
          MetricVal: { tag: 'span', text: '~₾5', fontSize: 'F', fontWeight: '700', color: 'green' },
          MetricLbl: { text: '{{ pitchServerCost | polyglot }}', color: 'textMuted', fontSize: 'A' }
        }
      },
      BottomRow: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },
        Metric4: {
          ...cardBase,
          MetricVal: { tag: 'span', text: '~86%', fontSize: 'F', fontWeight: '700', color: 'green' },
          MetricLbl: { text: 'Gross Margin', color: 'textMuted', fontSize: 'A' }
        },
        Metric5: {
          ...cardBase,
          MetricVal: { tag: 'span', text: '₾0', fontSize: 'F', fontWeight: '700', color: 'green' },
          MetricLbl: { text: '{{ pitchCacOrganic | polyglot }}', color: 'textMuted', fontSize: 'A' }
        },
        Metric6: {
          ...cardBase,
          MetricVal: { tag: 'span', text: 'SaaS', fontSize: 'E', fontWeight: '700', color: 'green' },
          MetricLbl: { text: '{{ pitchRecurringModel | polyglot }}', color: 'textMuted', fontSize: 'A' }
        }
      }
    },

    // Slide 9 — Why now
    Slide9: {
      ...slideBase,
      ...slideAnim(9),
      SlideLabel: {
        text: '{{ pitchTimeLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchTimeTitle | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      WhyList: {
        flow: 'y',
        gap: 'A',
        maxWidth: 'H',
        width: '100%',
        Why1: {
          ...whyBase,
          WhyNum: { ...whyBase.WhyNum, text: '01' },
          WhyDesc: { ...whyBase.WhyDesc, text: '{{ pitchWhyAiCheap | polyglot }}' }
        },
        Why2: {
          ...whyBase,
          WhyNum: { ...whyBase.WhyNum, text: '02' },
          WhyDesc: { ...whyBase.WhyDesc, text: '{{ pitchWhyDigitizing | polyglot }}' }
        },
        Why3: {
          ...whyBase,
          WhyNum: { ...whyBase.WhyNum, text: '03' },
          WhyDesc: { ...whyBase.WhyDesc, text: '{{ pitchWhyNoCompetitor | polyglot }}' }
        }
      }
    },

    // Slide 10 — Privacy & Infrastructure
    Slide10: {
      ...slideBase,
      ...slideAnim(10),
      SlideLabel: {
        text: '{{ pitchPrivacyLabel | polyglot }}',
        fontSize: 'A',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      },
      SlideTitle: {
        tag: 'h2',
        text: '{{ pitchPrivacyTitle | polyglot }}',
        fontSize: 'F',
        fontWeight: '700',
        color: 'text',
        '@mobileL': { fontSize: 'D' }
      },
      SlideSubtitle: {
        text: '{{ pitchPrivacySubtitle | polyglot }}',
        fontSize: 'B',
        color: 'textMuted',
        maxWidth: 'H',
        lineHeight: '1.6'
      },
      InfraRow: {
        flow: 'x',
        gap: 'A',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },
        InfraCloud: {
          ...cardBase,
          background: 'greenBg',
          border: '2px solid green',
          InfraIcon: { text: '☁️', fontSize: 'D' },
          InfraTitle: { tag: 'h3', text: '{{ pitchCloudDedicated | polyglot }}', fontSize: 'B', fontWeight: '700', color: 'text' },
          InfraDesc: { text: '{{ pitchCloudDedicatedDesc | polyglot }}', color: 'textSoft', fontSize: 'A', lineHeight: '1.6' }
        },
        InfraOnPrem: {
          ...cardBase,
          InfraIcon: { text: '🏢', fontSize: 'D' },
          InfraTitle: { tag: 'h3', text: '{{ pitchOnPremise | polyglot }}', fontSize: 'B', fontWeight: '700', color: 'text' },
          InfraDesc: { text: '{{ pitchOnPremiseDesc | polyglot }}', color: 'textSoft', fontSize: 'A', lineHeight: '1.6' }
        },
        InfraHybrid: {
          ...cardBase,
          InfraIcon: { text: '🔒', fontSize: 'D' },
          InfraTitle: { tag: 'h3', text: '{{ pitchHybrid | polyglot }}', fontSize: 'B', fontWeight: '700', color: 'text' },
          InfraDesc: { text: '{{ pitchHybridDesc | polyglot }}', color: 'textSoft', fontSize: 'A', lineHeight: '1.6' }
        }
      },
      PrivacyFeatures: {
        flow: 'x',
        gap: 'Z',
        flexWrap: 'wrap',
        align: 'stretch center',
        width: '100%',
        maxWidth: 'J',
        '@mobileL': { flow: 'y' },
        Feat1: {
          flow: 'x', gap: 'Z', align: 'center', padding: 'Z A', background: 'surfaceCard', border: '1px solid surfaceBorderStrong', borderRadius: 'Z',
          FeatDot: { width: 'Y', height: 'Y', borderRadius: '100%', background: 'green', flexShrink: '0' },
          FeatText: { text: 'End-to-end encryption', fontSize: 'Z', color: 'text' }
        },
        Feat2: {
          flow: 'x', gap: 'Z', align: 'center', padding: 'Z A', background: 'surfaceCard', border: '1px solid surfaceBorderStrong', borderRadius: 'Z',
          FeatDot: { width: 'Y', height: 'Y', borderRadius: '100%', background: 'green', flexShrink: '0' },
          FeatText: { text: '{{ pitchGdprCompliant | polyglot }}', fontSize: 'Z', color: 'text' }
        },
        Feat3: {
          flow: 'x', gap: 'Z', align: 'center', padding: 'Z A', background: 'surfaceCard', border: '1px solid surfaceBorderStrong', borderRadius: 'Z',
          FeatDot: { width: 'Y', height: 'Y', borderRadius: '100%', background: 'green', flexShrink: '0' },
          FeatText: { text: '{{ pitchDedicatedServer | polyglot }}', fontSize: 'Z', color: 'text' }
        },
        Feat4: {
          flow: 'x', gap: 'Z', align: 'center', padding: 'Z A', background: 'surfaceCard', border: '1px solid surfaceBorderStrong', borderRadius: 'Z',
          FeatDot: { width: 'Y', height: 'Y', borderRadius: '100%', background: 'green', flexShrink: '0' },
          FeatText: { text: '{{ pitchAutoBackup | polyglot }}', fontSize: 'Z', color: 'text' }
        }
      }
    },

    // Slide 11 — CTA
    Slide11: {
      ...slideBase,
      ...slideAnim(11),
      gap: 'B',
      TagLine: {
        fontSize: 'Z',
        color: 'green',
        fontWeight: '600',
        text: '{{ pitchCtaLabel | polyglot }}',
        letterSpacing: '1px',
        textTransform: 'uppercase'
      },
      TitleText: {
        tag: 'h2',
        text: '{{ pitchCtaTitle | polyglot }}',
        fontSize: 'G',
        fontWeight: '700',
        color: 'text',
        lineHeight: '1.1',
        maxWidth: 'J',
        '@mobileL': { fontSize: 'E' }
      },
      SubText: {
        text: '{{ pitchCtaSubtext | polyglot }}',
        fontSize: 'D',
        color: 'green',
        fontWeight: '600'
      },
      CtaLink: {
        extends: 'Link',
        href: '/',
        text: '{{ pitchCtaLink | polyglot }}',
        background: 'green',
        color: 'black',
        padding: 'A B',
        fontSize: 'B',
        fontWeight: '600',
        borderRadius: 'Y',
        textDecoration: 'none',
        marginTop: 'A',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' }
      }
    }
  },

  // Navigation
  NavRow: {
    position: 'absolute',
    bottom: 'A',
    left: '0',
    right: '0',
    flow: 'x',
    align: 'center center',
    gap: 'A',
    zIndex: '10',
    PrevBtn: {
      extends: 'Button',
      text: '←',
      background: 'transparent',
      color: 'textSoft',
      border: 'none',
      fontSize: 'B',
      padding: 'Z A',
      cursor: 'pointer',
      opacity: (el, s) => (s.slide > 0 ? '1' : '0.2'),
      pointerEvents: (el, s) => (s.slide > 0 ? 'auto' : 'none'),
      onClick: (e, el, s) => s.update({ slide: s.slide - 1 })
    },
    DotsWrap: {
      flow: 'x',
      gap: 'Y',
      align: 'center center',
      childExtends: {
        width: 'Y',
        height: 'Y',
        borderRadius: '100%',
        cursor: 'pointer',
        transition: 'background 200ms'
      },
      Dot0: { background: (el, s) => s.slide === 0 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 0 }) },
      Dot1: { background: (el, s) => s.slide === 1 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 1 }) },
      Dot2: { background: (el, s) => s.slide === 2 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 2 }) },
      Dot3: { background: (el, s) => s.slide === 3 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 3 }) },
      Dot4: { background: (el, s) => s.slide === 4 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 4 }) },
      Dot5: { background: (el, s) => s.slide === 5 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 5 }) },
      Dot6: { background: (el, s) => s.slide === 6 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 6 }) },
      Dot7: { background: (el, s) => s.slide === 7 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 7 }) },
      Dot8: { background: (el, s) => s.slide === 8 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 8 }) },
      Dot9: { background: (el, s) => s.slide === 9 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 9 }) },
      Dot10: { background: (el, s) => s.slide === 10 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 10 }) },
      Dot11: { background: (el, s) => s.slide === 11 ? 'green' : 'surfaceBorderStrong', onClick: (e, el, s) => s.update({ slide: 11 }) }
    },
    NextBtn: {
      extends: 'Button',
      text: '→',
      background: 'transparent',
      color: 'textSoft',
      border: 'none',
      fontSize: 'B',
      padding: 'Z A',
      cursor: 'pointer',
      opacity: (el, s) => (s.slide < s.total - 1 ? '1' : '0.2'),
      pointerEvents: (el, s) => (s.slide < s.total - 1 ? 'auto' : 'none'),
      onClick: (e, el, s) => s.update({ slide: s.slide + 1 })
    }
  }
}
